import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import * as yup from "yup";
import { updateSubOrganizations, getSubOrganizationById } from "../../../../../../../api";
import TextInput from "../../../../../../components/form/text-input";
import Modal from "../../../../../../components/modal";
import ConfirmationModal from "../../../../../application/components/ConfirmationModal/ConfirmationModal";
import Button from "../../../../../../components/new-button";
import useOrganizationStore from "../../../../../../stores/organization";
import layoutStyles from "../../../../../../styles/layout.module.scss";
import useSubOrganizations from "../../hooks/useSubOrganizations";
import AddLogo from "../../../../components/add-organization-modal/components/add-logo";
import config from "../../../../../../../../js/config";

const schema = yup.object({
  sub_org_name: yup
    .string()
    .trim()
    .required("Sub-organization name is required"),
  fileToUpload: yup.mixed().nullable(),
});

const EditSubOrganizationModal = ({ open, onClose, subOrgId }) => {
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.id,
  );
  const updateSubOrgs = useOrganizationStore((state) => state.updateSubOrgs);
  const [logoUrl, setLogoUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({id: subOrgId});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const formikRef = useRef(null);

  const fetchSubOrgById = async () => {
    if(!subOrgId){
      return;
    }
    setIsLoading(true);
    try{
      const response = await getSubOrganizationById(subOrgId);
      if (response.success) {
          setInitialValues(prev => ({
            ...prev,
            id: response.data.id,
            sub_org_name: response.data.sub_org_name,
            logo_path: response?.data?.logo_path
          }));

        if(response?.data?.logo_path){
          setLogoUrl(response?.data?.logo_path);
        }
      }
    }catch (e) {
      toast.error("Failed to load sub-organization", { duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubOrgById();
  }, [subOrgId]);

  const handleBackdropClose = () => {
    if (formikRef.current?.dirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleEditSubOrgFormSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();

    formData.append('id', values.id);
    formData.append('sub_organization_name', values.sub_org_name);
    formData.append('organization_id', selectedOrganizationId);

    if (values.fileToUpload) {
      formData.append('fileToUpload', values.fileToUpload);
    }

    const toastId = toast.loading("Updating sub-organization...");
    try {
      const res = await updateSubOrganizations(formData);
      if (!res?.status) {
        console.error("Something wrong, please try again.");
      } else {
        onClose();
        toast.success("Sub-organization updated successfully", {
          id: toastId,
          duration: 5000,
        });
        setInitialValues(prev => ({
          ...prev,
          id: res.data.id,
          sub_org_name: res.data.sub_org_name,
          logo_path: res?.data?.logo_path
        }));
        if(res?.data?.logo_path){
          setLogoUrl(res?.data?.logo_path);
        }
      }
    } catch (err) {
      if (err.status === 422) {
        toast.error("Validation error. Please fill all required fields", {
          id: toastId,
          duration: 5000,
        });
      } else {
        toast.error("Something went wrong. Please try again.", { duration: 5000 });
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        confirmClose={handleBackdropClose}
        header="Edit Sub-organization"
        size="sm"
      >
        <Formik
          innerRef={formikRef}
          enableReinitialize
          validationSchema={schema}
          onSubmit={handleEditSubOrgFormSubmit}
          initialValues={initialValues}
          validateOnBlur={false}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            isSubmitting,
          }) => (
            <form autoComplete="off" onSubmit={handleSubmit}>
              
            <AddLogo
              imageSrc={logoUrl || values.logo_path}
            />
              <div className={`${layoutStyles.grid} ${layoutStyles.gap2}`}>
                <div
                  className={`${layoutStyles.grid} ${layoutStyles.gridCols1} ${layoutStyles.gap2}`}
                >
                  <TextInput
                    label="Sub-organization name"
                    id="sub_org_name"
                    name="sub_org_name"
                    value={values.sub_org_name || ""}
                    onChange={handleChange}
                    touched={touched.sub_org_name}
                    errored={errors.sub_org_name}
                    error={errors["sub_org_name"]}
                  />
                </div>
                <div className={`${layoutStyles.grid} ${layoutStyles.gap2}`}>
                  <div
                    className={`${layoutStyles.flex} ${layoutStyles.flexEnd} ${layoutStyles.gap1}`}
                  >
                    <Button type="button" variant="ghost" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </Modal>
      <ConfirmationModal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirmClick={handleDiscardConfirm}
        message="You have unsaved changes. Discard them?"
        submitButtonText="Discard"
      />
    </>
  );
};

export default EditSubOrganizationModal;
